import {
  CurrencyEnum,
  DateRangePicker,
  FormInputSearch,
  FormSelect,
  Hint,
  PrimaryButton,
  cents,
  centsToUnits,
  toast,
  units,
} from '@systemeio/ui'
import {
  AFFILIATE_PROGRAM_API,
  updateCommission,
} from 'modules/affiliate-program/api/affiliate-program-api'
import {
  AffiliateCommissionInput,
  AffiliatePayoutInput,
} from 'modules/affiliate-program/components/affiliate-input'
import AffiliateStatisticSkeleton from 'modules/affiliate-program/components/affiliate-statistic-skeleton'
import { AffiliateProgramLoco } from 'modules/affiliate-program/constants/affiliate-program-loco'
import { useAffiliateStatistic } from 'modules/affiliate-program/hooks/use-affiliate-statistic'
import { useAffiliates } from 'modules/affiliate-program/hooks/use-affiliates'
import { useCommissions } from 'modules/affiliate-program/hooks/use-commissions'
import {
  AffiliateFilterInterface,
  AffiliateInterface,
  CommissionInterface,
} from 'modules/affiliate-program/types/affiliate-program-interface'
import { getAffiliateSum } from 'modules/affiliate-program/utils/get-affiliate-sum'
import { getFunnelStepUrl } from 'modules/funnels/funnel/utils/get-funnel-step-url'
import { NextPage } from 'next'
import React, { useEffect, useState } from 'react'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useUpdateData } from 'shared/hooks/use-update-data'
import useUser from 'shared/hooks/use-user'
import { useFilterWithDate } from 'shared/route-query/hooks/use-filter/use-filter-with-date'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import { currencyFormat } from 'shared/utils/currency-format'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { getCurrencySymbol } from 'shared/utils/get-currency-symbol'
import { getThisMonthDateRange, getToday } from 'shared/utils/get-dates'
import { SettingsIcon } from '@systemeio/ui/icons'

const defaultFilters: AffiliateFilterInterface = {
  email: '',
  id: '',
  dateSince: null,
  dateTill: null,
}

const AffiliateProgram: NextPage = () => {
  const { filter: affiliatesFilter, setFilter: setAffiliatesFilter } = useFilterWithDate(
    defaultFilters,
    getThisMonthDateRange,
  )

  const {
    setPagination: setPaginationAffiliates,
    pagination: paginationAffiliates,
    setLimit: setLimitAffiliates,
    limit: limitAffiliates,
  } = usePagination(true)

  const {
    setPagination: setPaginationCommissions,
    pagination: paginationCommissions,
    setLimit: setLimitCommissions,
    limit: limitCommissions,
  } = usePagination(true)

  const { t } = useLocoTranslation()
  const { user, intlLocale } = useUser()

  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyEnum | undefined>(undefined)

  const [currencyData, setCurrencyData] = useState<
    { id: CurrencyEnum; caption: string }[] | undefined
  >()

  const { affiliates, affiliatesCounter } = useAffiliates({
    limit: limitAffiliates,
    pagination: paginationAffiliates,
    filter: affiliatesFilter,
  })
  const { commissions, mutateCommissions, commissionsCounter } = useCommissions({
    limit: limitCommissions,
    pagination: paginationCommissions,
  })
  const { affiliateStatistic } = useAffiliateStatistic({ filter: affiliatesFilter })

  const updateCommissionData = useUpdateData({
    api: `${AFFILIATE_PROGRAM_API}/affiliate-commissions`,
  })

  useEffect(() => {
    if (user && affiliatesFilter.dateSince === null) {
      const [defaultDateSince, defaultDateTill] = getThisMonthDateRange(user.timezone)
      setAffiliatesFilter(prev => ({
        ...prev,
        dateSince: defaultDateSince,
        dateTill: defaultDateTill,
      }))
    }
  }, [user, affiliatesFilter, setAffiliatesFilter])

  useEffect(() => {
    if (affiliateStatistic && !Array.isArray(affiliateStatistic.commonAffiliateSalesInfo) && user) {
      const currencies = Object.keys(affiliateStatistic.commonAffiliateSalesInfo) as CurrencyEnum[]
      setCurrencyData(
        currencies.map(el => ({ id: el, caption: getCurrencySymbol(el, intlLocale) })),
      )
      setSelectedCurrency(
        currencies.includes(CurrencyEnum.EURO) ? CurrencyEnum.EURO : currencies[0],
      )
    } else {
      setCurrencyData(undefined)
      setSelectedCurrency(undefined)
    }
  }, [affiliateStatistic, intlLocale])

  const [isPayoutFetching, setIsPayoutFetching] = useState(false)
  const [isCommissionFetching, setIsCommissionFetching] = useState(false)

  const onSavePayoutDelay = async (commission: CommissionInterface, value: number) => {
    try {
      setIsPayoutFetching(true)
      await updateCommission(commission.id, {
        affiliateCommission: commission.affiliateCommission,
        payoutDelay: value,
      })
      await updateCommissionData(false, undefined, {
        mutationData: commissions,
        onMutate: async () => {
          await mutateCommissions(data => {
            if (data) {
              return {
                items: data.items.map(item => {
                  if (item.id === commission.id) item = { ...item, payoutDelay: value }
                  return item
                }),
                hasMore: data.hasMore,
              }
            }
          }, false)
        },
      })

      setIsPayoutFetching(false)
      toast.success(t('dashboard.affiliate_program.payout_delay_changed'))
    } catch (e) {
      setIsPayoutFetching(false)
      toast.error(t('global.error'))
    }
  }

  const onSaveCommission = async (commission: CommissionInterface, value: number) => {
    try {
      setIsCommissionFetching(true)
      await updateCommission(commission.id, {
        payoutDelay: commission.payoutDelay,
        affiliateCommission: value,
      })
      await updateCommissionData(false, undefined, {
        mutationData: commissions,
        onMutate: async () => {
          await mutateCommissions(data => {
            if (data) {
              return {
                items: data.items.map(item => {
                  if (item.id === commission.id) item = { ...item, affiliateCommission: value }
                  return item
                }),
                hasMore: data.hasMore,
              }
            }
          }, false)
        },
      })

      setIsCommissionFetching(false)
      toast.success(t('dashboard.affiliate_program.affiliate_commission_changed'))
    } catch (e) {
      setIsCommissionFetching(false)
      toast.error(t('global.error'))
    }
  }

  const columnsAffiliates: ColumnDefinitionInterface<AffiliateInterface>[] = [
    {
      key: 'email',
      header: t('dashboard.affiliate_program.affiliate_email'),
      widthPercent: 30,
      onRender: affiliate => (
        <div className="truncate">
          {affiliate.userId ? (
            <LinkWithoutPrefetch
              className="primary-link w-full"
              href={`/affiliate-program/${affiliate.affiliateId}`}
            >
              {affiliate.email}
            </LinkWithoutPrefetch>
          ) : (
            <span className={'w-full'}>{affiliate.email}</span>
          )}
        </div>
      ),
    },
    {
      key: 'sums',
      header: t('dashboard.affiliate_program.sum_of_sales'),
      widthPercent: 30,
      onRender: affiliate => (
        <div className={'flex flex-col gap-1 truncate'}>
          <span className={'truncate'}>
            {t('dashboard.affiliate_program.total_sales')}:{' '}
            {getAffiliateSum(affiliate.sums, selectedCurrency, user?.dashboardLocale)}
          </span>
          <span className={'truncate'}>
            {t('dashboard.affiliate_program.total_sales_after_refund')}:{' '}
            {getAffiliateSum(affiliate.sumsRefunded, selectedCurrency, user?.dashboardLocale)}
          </span>
        </div>
      ),
    },
    {
      key: 'commissions',
      header: t('dashboard.affiliate_program.total_commissions'),
      widthPercent: 30,
      onRender: affiliate => (
        <div className={'flex flex-col gap-1 truncate'}>
          <span className={'truncate'}>
            {t('dashboard.affiliate_program.total_commissions')}:{' '}
            {getAffiliateSum(affiliate.commissions, selectedCurrency, user?.dashboardLocale)}
          </span>
          <span className={'truncate'}>
            {t('dashboard.affiliate_program.total_commissions_after_refund')}:{' '}
            {getAffiliateSum(
              affiliate.sumsRefundedCommissions,
              selectedCurrency,
              user?.dashboardLocale,
            )}
          </span>
        </div>
      ),
    },
    {
      key: 'leads',
      header: t('dashboard.affiliate_program.num_of_leads_sent'),
      widthPercent: 10,
    },
  ]

  const columnsCommissions: ColumnDefinitionInterface<CommissionInterface>[] = [
    {
      key: 'id',
      header: t('dashboard.affiliate_program.offer_name'),
      widthPercent: 60,
      onRender: commission => (
        <div className="truncate">
          <LinkWithoutPrefetch
            className="primary-link w-full"
            href={getFunnelStepUrl(commission.funnelStep.funnel.id, commission.funnelStep.id)}
          >
            {`${commission.funnelStep.name} (${commission.funnelStep.funnel.name})`}
          </LinkWithoutPrefetch>
        </div>
      ),
    },
    {
      key: 'payoutDelay',
      header: t('dashboard.affiliate_program.payout_delay'),
      widthPercent: 20,
      onRender: commission => (
        <AffiliatePayoutInput
          isFetching={isPayoutFetching || isCommissionFetching}
          defaultValue={commission.payoutDelay}
          onSave={value => onSavePayoutDelay(commission, value || 0)}
        />
      ),
    },
    {
      key: 'affiliateCommission',
      header: t('dashboard.affiliate_program.affiliate_commission'),
      widthPercent: 20,
      onRender: commission => (
        <AffiliateCommissionInput
          isFetching={isCommissionFetching || isPayoutFetching}
          defaultValue={commission.affiliateCommission}
          onSave={value => onSaveCommission(commission, value || 0)}
        />
      ),
    },
  ]

  return (
    <div className={'flex flex-col gap-5'}>
      <div className={`flex flex-col justify-between gap-5 lg:gap-3 xl:flex-row`}>
        <div className={`flex w-full justify-between`}>
          <h1 className={`flex h-[42px] items-center whitespace-nowrap`}>
            {t('dashboard.affiliate_program.title')}
          </h1>
          <div className={'flex flex-col gap-3 xl:flex-row'}>
            <PrimaryButton
              as={LinkWithoutPrefetch}
              href={'/profile/affiliate-program-settings'}
              className={'flex justify-end xl:hidden'}
              aria-disabled={!user}
            >
              <SettingsIcon className={'fill-background-secondary'} />
              <span className="hidden lg:flex">
                {t('dashboard.affiliate_program.affiliate-program_settings')}
              </span>
            </PrimaryButton>
            <div className={'hidden gap-3 xl:flex'}>
              <DateRangePicker
                maxDate={getToday(user?.timezone)}
                disabled={!affiliateStatistic || !affiliates}
                getDefaultDate={() => getThisMonthDateRange(user?.timezone)}
                key={'affiliate-date-filter'}
                date={[affiliatesFilter.dateSince, affiliatesFilter.dateTill]}
                onChange={date =>
                  setAffiliatesFilter(prev => ({ ...prev, dateSince: date[0], dateTill: date[1] }))
                }
                timezone={user?.timezone}
                createdAt={user?.createdAt}
                intlLocale={intlLocale}
              />
              <FormSelect
                disabled={affiliateStatistic && !selectedCurrency}
                className={'max-w-[120px]'}
                data={currencyData}
                placeholder={t('global.currency')}
                withoutCloseIcon
                value={selectedCurrency}
                onChange={data => setSelectedCurrency(data)}
              />
              <PrimaryButton
                as={LinkWithoutPrefetch}
                href={'/profile/affiliate-program-settings'}
                aria-disabled={!user}
              >
                <SettingsIcon className={'fill-background-secondary'} />
                <span className="hidden lg:flex">
                  {t('dashboard.affiliate_program.affiliate-program_settings')}
                </span>
              </PrimaryButton>
            </div>
          </div>
        </div>
        <div className={'flex flex-wrap justify-end gap-3 xl:hidden'}>
          <DateRangePicker
            maxDate={getToday(user?.timezone)}
            disabled={!affiliateStatistic || !affiliates}
            getDefaultDate={() => getThisMonthDateRange(user?.timezone)}
            key={'affiliate-date-filter'}
            date={[affiliatesFilter.dateSince, affiliatesFilter.dateTill]}
            onChange={date =>
              setAffiliatesFilter(prev => ({ ...prev, dateSince: date[0], dateTill: date[1] }))
            }
            timezone={user?.timezone}
            createdAt={user?.createdAt}
            intlLocale={intlLocale}
          />
          <FormSelect
            disabled={affiliateStatistic && !selectedCurrency}
            className={'max-w-[100px]'}
            data={currencyData}
            placeholder={t('global.currency')}
            withoutCloseIcon
            value={selectedCurrency}
            onChange={data => setSelectedCurrency(data)}
          />
        </div>
      </div>
      <div className={'flex-col rounded-lg'}>
        <div className={'bg-brand-primary-subtle flex rounded-t-lg px-6 py-4'}>
          <span
            className={
              'font-inter text-text-primary flex w-1/3 justify-center text-sm font-bold sm:text-[17px]'
            }
          >
            {t('dashboard.affiliate_program.total_sales')}
          </span>
          <Hint className='flex w-1/3 justify-center' label={t('dashboard.affiliate_program.total_leads_sent_hint')}>
            <span
              className={
                'font-inter text-text-primary text-sm font-bold sm:text-[17px]'
              }
            >
              {t('dashboard.affiliate_program.total_leads_sent')}
            </span>
          </Hint>
          <span
            className={
              'font-inter text-text-primary flex w-1/3 justify-center text-sm font-bold sm:text-[17px]'
            }
          >
            {t('dashboard.affiliate_program.total_commissions')}
          </span>
        </div>
        {affiliateStatistic ? (
          <div
            className={
              'bg-background-secondary text-brand-primary flex rounded-b-lg px-6 py-4 text-sm font-bold sm:text-3xl'
            }
          >
            <span className={'flex w-1/3 justify-center'}>
              {selectedCurrency
                ? currencyFormat(
                    Array.isArray(affiliateStatistic.commonAffiliateSalesInfo)
                      ? units(0)
                      : centsToUnits(
                          cents(
                            affiliateStatistic.commonAffiliateSalesInfo[selectedCurrency]
                              .total_sum_after_refund,
                          ),
                        ),
                    selectedCurrency,
                    user?.dashboardLocale,
                  )
                : currencyFormat(units(0), CurrencyEnum.EURO, user?.dashboardLocale)}
            </span>
            <span className={'flex w-1/3 justify-center'}>{affiliateStatistic.leadsCount}</span>
            <span className={'flex w-1/3 justify-center'}>
              {selectedCurrency
                ? currencyFormat(
                    Array.isArray(affiliateStatistic.commonAffiliateSalesInfo)
                      ? units(0)
                      : centsToUnits(
                          cents(
                            affiliateStatistic.commonAffiliateSalesInfo[selectedCurrency]
                              .affiliate_commission_after_refund,
                          ),
                        ),
                    selectedCurrency,
                    user?.dashboardLocale,
                  )
                : currencyFormat(units(0), CurrencyEnum.EURO, user?.dashboardLocale)}
            </span>
          </div>
        ) : (
          <AffiliateStatisticSkeleton />
        )}
      </div>
      <div>
        <ListingLayout
          data={affiliates}
          counter={affiliatesCounter}
          title={t('dashboard.affiliate_program.affiliates')}
          type={'default'}
          filterData={{
            limiter: { limit: limitAffiliates, setLimit: setLimitAffiliates },
            paginator: {
              pagination: paginationAffiliates,
              setPagination: setPaginationAffiliates,
            },
            filterer: { defaultFilters, filter: affiliatesFilter, setFilter: setAffiliatesFilter },
            filterable: [
              {
                field: 'email',
                onRender: (value, onChange) => (
                  <FormInputSearch
                    key={'affiliate-email-filter'}
                    value={value}
                    placeholder={t('dashboard.affiliate_program.affiliate_email')}
                    onChange={e => onChange(e.target.value)}
                    onClear={() => onChange('')}
                  />
                ),
              },
              {
                field: 'id',
                onRender: (value, onChange) => (
                  <FormInputSearch
                    key={'affiliate-id-filter'}
                    value={value}
                    placeholder={t('dashboard.affiliate_program.affiliate_id')}
                    onChange={e => onChange(e.target.value)}
                    onClear={() => onChange('')}
                  />
                ),
              },
            ],
          }}
        >
          <Table
            data={affiliates}
            columns={columnsAffiliates}
            paginator={{
              pagination: paginationAffiliates,
              setPagination: setPaginationAffiliates,
            }}
            limiter={{ limit: limitAffiliates, setLimit: setLimitAffiliates }}
          />
        </ListingLayout>
      </div>
      <div>
        <ListingLayout
          data={commissions}
          title={t('dashboard.affiliate_program.affiliate_commission_structure')}
          type={'compact'}
          counter={commissionsCounter}
        >
          <Table
            paginator={{
              pagination: paginationCommissions,
              setPagination: setPaginationCommissions,
            }}
            limiter={{ limit: limitCommissions, setLimit: setLimitCommissions }}
            data={commissions}
            columns={columnsCommissions}
          />
        </ListingLayout>
      </div>
    </div>
  )
}

export const getServerSideProps = getBaseServerSideProps(
  AffiliateProgramLoco,
  'header.top_menu.affiliate_program',
)

export default AffiliateProgram
